import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DateRangePicker from '../components/layout/DateRangePicker';
import { loadDateRange, saveDateRange } from '../utils/dateRangePersistence';
import DashboardLayout from '../components/layout/DashboardLayout';
import Container from '../components/layout/Container';
import TicketList from '../components/tickets/TicketList';
import TicketTable from '../components/tickets/TicketTable';
import { ticketService } from '../services/ticketService';
import Pagination from '../components/common/Pagination';
import { useTickets } from '../hooks/useTickets';
import { Status } from '../types';
import TicketKanbanBoard from '../components/tickets/TicketKanbanBoard';
import { useTheme } from '../contexts/ThemeContext';
import { Tag } from '../types/tag';
import { tagService } from '../services/tagService';
import ThemedAsyncSelect from '../components/common/ThemedAsyncSelect';
import { TICKET_STATUSES } from '../constants/ticketConstants';
import { userService } from '../services/userService';

export default function TicketsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const [dateRange, setDateRange] = useState(loadDateRange('tickets-date-range'));
  const [itemsPerPage, setItemsPerPage] = useState<number>(() => {
    const saved = localStorage.getItem('ticketsPageLimit');
    return saved ? parseInt(saved, 10) : 12;
  });

  // Save date range whenever it changes
  useEffect(() => {
    saveDateRange('tickets-date-range', dateRange);
  }, [dateRange]);

  // Get filter states from location state or localStorage
  const filterState = location.state?.filterState || {
    selectedStatus: localStorage.getItem('ticketsSelectedStatus') || '',
    searchQuery: localStorage.getItem('ticketsSearchQuery') || '',
    sortBy: localStorage.getItem('ticketsSortBy') || 'createdAt',
    sortOrder: localStorage.getItem('ticketsSortOrder') || 'desc',
    currentPage: parseInt(localStorage.getItem('ticketsCurrentPage') || '1')
  };

  const [selectedStatus, setSelectedStatus] = useState<string>(filterState.selectedStatus);
  const [searchQuery, setSearchQuery] = useState(filterState.searchQuery);
  const [sortBy, setSortBy] = useState(filterState.sortBy);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(
    filterState.sortOrder as 'asc' | 'desc'
  );
  const [currentPage, setCurrentPage] = useState(filterState.currentPage);

  // Track layout mode (default is 'kanban')
  const [viewMode, setViewMode] = useState<'list' | 'kanban' | 'table'>(() => {
    const savedMode = localStorage.getItem('ticketsViewMode');
    return savedMode === 'list' || savedMode === 'kanban' || savedMode === 'table'
      ? savedMode
      : 'kanban';
  });

  // Tags filter state
  const [selectedTags, setSelectedTags] = useState<string[]>(() =>
    JSON.parse(localStorage.getItem('ticketsSelectedTags') || '[]')
  );
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [showFilters, setShowFilters] = useState<boolean>(true);

  // User filter states
  const [requestedBy, setRequestedBy] = useState<any[]>(() =>
    JSON.parse(localStorage.getItem('ticketsRequestedBy') || '[]')
  );
  const [assignedTo, setAssignedTo] = useState<any[]>(() =>
    JSON.parse(localStorage.getItem('ticketsAssignedTo') || '[]')
  );

  // Load all available tags
  useEffect(() => {
    const loadTags = async () => {
      try {
        const tags = await tagService.getAllTags();
        setAllTags(tags);
      } catch (error) {
        console.error('Error loading tags:', error);
        setAllTags([]);
      }
    };
    loadTags();
  }, []);

  // Store filter states in location state when navigating to ticket detail
  const handleTicketClick = (ticketId: number) => {
    navigate(`/tickets/${ticketId}`, {
      state: {
        from: '/tickets',
        filterState: {
          selectedStatus,
          searchQuery,
          sortBy,
          sortOrder,
          currentPage
        }
      }
    });
  };

  // Update filter states when location.state changes
  useEffect(() => {
    if (location.state?.filterState) {
      const {
        selectedStatus: newStatus,
        searchQuery: newQuery,
        sortBy: newSort,
        sortOrder: newOrder,
        currentPage: newPage
      } = location.state.filterState;

      setSelectedStatus(newStatus || '');
      setSearchQuery(newQuery || '');
      setSortBy(newSort || 'createdAt');
      setSortOrder(newOrder || 'desc');
      setCurrentPage(newPage || 1);
    }
  }, [location.state]);

  // Save filter states to localStorage when they change
  useEffect(() => {
    localStorage.setItem('ticketsSelectedStatus', selectedStatus);
    localStorage.setItem('ticketsSearchQuery', searchQuery);
    localStorage.setItem('ticketsSortBy', sortBy);
    localStorage.setItem('ticketsSortOrder', sortOrder);
    localStorage.setItem('ticketsCurrentPage', currentPage.toString());
    localStorage.setItem('ticketsSelectedTags', JSON.stringify(selectedTags));
    localStorage.setItem('ticketsRequestedBy', JSON.stringify(requestedBy));
    localStorage.setItem('ticketsAssignedTo', JSON.stringify(assignedTo));
  }, [
    selectedStatus,
    searchQuery,
    sortBy,
    sortOrder,
    currentPage,
    selectedTags,
    requestedBy,
    assignedTo
  ]);

  // Store current page in localStorage when it changes
  useEffect(() => {
    localStorage.setItem('ticketsPage', currentPage.toString());
  }, [currentPage]);

  // On mount and when viewMode changes, save it to localStorage
  useEffect(() => {
    localStorage.setItem('ticketsViewMode', viewMode);
  }, [viewMode]);

  // Filter out invalid user objects and map to valid IDs
  const requestedByIds =
    requestedBy.length > 0
      ? requestedBy
        .filter(
          (user) =>
            user &&
            typeof user === 'object' &&
            (user.id || user.value) &&
            (typeof user.id === 'number' ||
              typeof user.value === 'string' ||
              typeof user.value === 'number')
        )
        .map((user) => {
          // Use either id or value property depending on which exists
          return user.id || user.value;
        })
      : undefined;

  // Filter out invalid user objects and map to valid IDs
  const assignedToIds =
    assignedTo.length > 0
      ? assignedTo
        .filter(
          (user) =>
            user &&
            typeof user === 'object' &&
            (user.id || user.value) &&
            (typeof user.id === 'number' ||
              typeof user.value === 'string' ||
              typeof user.value === 'number')
        )
        .map((user) => {
          // Use either id or value property depending on which exists
          return user.id || user.value;
        })
      : undefined;

  const { tickets, isLoading, stats, totalCount, fetchTickets } = useTickets({
    limit: itemsPerPage,
    dateRange,
    status: selectedStatus,
    search: searchQuery,
    sortBy,
    sortOrder,
    page: currentPage,
    tags: selectedTags.length > 0 ? selectedTags : undefined,
    requestedBy: requestedByIds,
    assignedTo: assignedToIds
  });

  const handleUpdateTicketStatus = async (ticketId: number, newStatus: Status, userId: number) => {
    if (!ticketId) {
      console.error('Cannot update ticket status: Invalid ticket ID');
      return;
    }

    try {
      // Update the ticket status via the API, passing the user ID
      await ticketService.updateStatus(ticketId, newStatus, userId);

      // Refetch tickets using the `fetchTickets` method from useTickets
      fetchTickets();
    } catch (error) {
      console.error('Error updating ticket status:', error);
    }
  };

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo(0, 0);
  };

  const handleItemsPerPageChange = (newLimit: number) => {
    setItemsPerPage(newLimit);
    setCurrentPage(1);
    localStorage.setItem('ticketsPageLimit', newLimit.toString());
  };

  const handleStatusChange = (status: string) => {
    setSelectedStatus(status);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const handleSortChange = (sort: string) => {
    setSortBy(sort);
    setCurrentPage(1); // Reset to first page when sort changes
  };

  const handleSortOrderChange = (order: 'asc' | 'desc') => {
    setSortOrder(order);
    setCurrentPage(1); // Reset to first page when sort order changes
  };

  const handleDateRangeChange = (start: string, end: string) => {
    setDateRange({ startDate: start, endDate: end });
    setCurrentPage(1); // Reset to first page when date range changes
  };

  // Handler for requestedBy filter changes
  const handleRequestedByChange = (selectedUsers: any) => {
    setRequestedBy(selectedUsers);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  // Handler for assignedTo filter changes
  const handleAssignedToChange = (selectedUsers: any) => {
    setAssignedTo(selectedUsers);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  // Load user options for filters
  const loadRequestedByOptions = async (inputValue: string) => {
    if (!inputValue) {
      const users = await userService.getUsers();
      return users.slice(0, 10).map((user: any) => ({
        label: `${user.full_name} (${user.email})`,
        value: user.id.toString()
      }));
    }

    const users = await userService.searchUsers(inputValue);
    return users.map((user: any) => ({
      label: `${user.full_name} (${user.email})`,
      value: user.id.toString()
    }));
  };

  // Load user options for filters
  const loadAssignedToOptions = async (inputValue: string) => {
    if (!inputValue) {
      const users = await userService.getUsers();
      const data = [{ value: 'unassigned', label: t('tickets.status.unassigned', 'Unassigned') }];
      return data.concat(
        users.slice(0, 10).map((user: any) => ({
          label: `${user.full_name} (${user.email})`,
          value: user.id.toString()
        }))
      );
    }

    const users = await userService.searchUsers(inputValue);

    const data = [{ value: 'unassigned', label: t('tickets.status.unassigned', 'Unassigned') }];
    return data.concat(
      users.map((user: any) => ({
        label: `${user.full_name} (${user.email})`,
        value: user.id.toString()
      }))
    );
  };

  // Handler for tags filter changes
  const handleTagsChange = (selectedTags: any) => {
    const tagNames = selectedTags ? selectedTags.map((tag: any) => tag.value) : [];
    setSelectedTags(tagNames);
    setCurrentPage(1); // Reset to first page when filter changes
    localStorage.setItem('ticketsSelectedTags', JSON.stringify(tagNames));
  };

  // Load tag options for filters
  const loadTagOptions = async (inputValue: string) => {
    // Wait a bit if tags are still loading
    if (allTags.length === 0) {
      // Return empty array if no tags are available
      return [];
    }

    // If no input value, return all tags (for initial load)
    if (!inputValue) {
      return allTags.map((tag: Tag) => ({
        label: tag.name,
        value: tag.name
      }));
    }

    // Filter tags based on input
    const filteredTags = allTags.filter((tag) =>
      tag.name.toLowerCase().includes(inputValue.toLowerCase())
    );

    return filteredTags.map((tag: Tag) => ({
      label: tag.name,
      value: tag.name
    }));
  };

  // Sync view mode with localStorage on toggle
  const toggleViewMode = (mode: 'list' | 'kanban' | 'table') => {
    setViewMode(mode);
    localStorage.setItem('ticketsViewMode', mode);
  };

  return (
    <DashboardLayout>
      <Container
        className={`py-6 space-y-6 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}
      >
        {/* Page Header */}
        <div className="flex flex-col gap-4 mb-4 sm:flex-row sm:justify-between sm:items-center">
          <h1
            className={`text-2xl font-bold ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}
          >
            {t('pages.tickets.title')}
          </h1>

          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="flex flex-col gap-2 sm:flex-row sm:gap-4">
              <button
                onClick={() => setShowFilters((prev) => !prev)}
                type="button"
                aria-pressed={showFilters}
                aria-label={
                  showFilters ? t('pages.tickets.hideFilters') : t('pages.tickets.showFilters')
                }
                className={`ml-3 w-9 h-9 flex items-center justify-center rounded-full border transition-all duration-200 ${showFilters
                    ? theme === 'dark'
                      ? 'bg-blue-600 text-white hover:bg-blue-700 border-blue-700 shadow-md'
                      : 'bg-blue-500 text-white hover:bg-blue-600 border-blue-600 shadow-md'
                    : theme === 'dark'
                      ? 'bg-zinc-800 text-white hover:bg-zinc-700 border-zinc-700'
                      : 'bg-white text-zinc-700 hover:bg-zinc-100 border-zinc-300'
                  }`}
              >
                <i
                  className={`fas ${showFilters ? 'fa-sliders' : 'fa-sliders-h'} ${showFilters ? 'animate-pulse' : ''}`}
                ></i>
              </button>
              <DateRangePicker
                startDate={dateRange.startDate}
                endDate={dateRange.endDate}
                onDateChange={handleDateRangeChange}
                disabled={isLoading}
              />
            </div>
          </div>
        </div>

        {/* Dashboard Analytics & Controls */}
        <div
          className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 transition-all duration-300 ${showFilters ? 'opacity-100' : 'opacity-0 h-0'}`}
          style={{
            transition: 'opacity 0.3s ease, height 0.3s ease',
            display: showFilters ? 'grid' : 'none'
          }}
        >
          {/* Total Stats */}
          <div className="relative overflow-hidden rounded-lg p-4 transition-all duration-300 hover:translate-y-[-2px] bg-blue-500/10 hover:bg-blue-500/15">
            <div className="flex items-center justify-between">
              <div>
                <p
                  className={`text-2xl font-bold ${theme === 'dark' ? 'text-blue-300' : 'text-blue-700'}`}
                >
                  {stats?.total || 0}
                </p>
                <p
                  className={`text-xs font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}
                >
                  {t('pages.tickets.totalTickets')}
                </p>
              </div>
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-blue-500/20">
                <i
                  className={`fas fa-ticket-alt ${theme === 'dark' ? 'text-blue-300' : 'text-blue-600'}`}
                ></i>
              </div>
            </div>
            <div className="absolute bottom-0 left-0 h-1 w-full bg-blue-500/40"></div>
          </div>

          {/* New Stats */}
          <div className="relative overflow-hidden rounded-lg p-4 transition-all duration-300 hover:translate-y-[-2px] bg-green-500/10 hover:bg-green-500/15">
            <div className="flex items-center justify-between">
              <div>
                <p
                  className={`text-2xl font-bold ${theme === 'dark' ? 'text-green-300' : 'text-green-700'}`}
                >
                  {stats?.new || 0}
                </p>
                <p
                  className={`text-xs font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}
                >
                  {t('tickets.status.new')}
                </p>
              </div>
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-green-500/20">
                <i
                  className={`fas fa-plus-circle ${theme === 'dark' ? 'text-green-300' : 'text-green-600'}`}
                ></i>
              </div>
            </div>
            <div className="absolute bottom-0 left-0 h-1 w-full bg-green-500/40"></div>
          </div>

          {/* Unassigned Stats */}
          <div className="relative overflow-hidden rounded-lg p-4 transition-all duration-300 hover:translate-y-[-2px] bg-neutral-500/10 hover:bg-neutral-500/15">
            <div className="flex items-center justify-between">
              <div>
                <p
                  className={`text-2xl font-bold ${theme === 'dark' ? 'text-neutral-300' : 'text-neutral-700'}`}
                >
                  {stats?.unassigned || 0}
                </p>
                <p
                  className={`text-xs font-medium ${theme === 'dark' ? 'text-neutral-300' : 'text-neutral-600'}`}
                >
                  {t('tickets.status.unassigned')}
                </p>
              </div>
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-neutral-500/20">
                <i
                  className={`fas fa-user-times ${theme === 'dark' ? 'text-neutral-300' : 'text-neutral-600'}`}
                ></i>
              </div>
            </div>
            <div className="absolute bottom-0 left-0 h-1 w-full bg-neutral-500/40"></div>
          </div>

          {/* Processing Stats */}
          <div className="relative overflow-hidden rounded-lg p-4 transition-all duration-300 hover:translate-y-[-2px] bg-amber-500/10 hover:bg-amber-500/15">
            <div className="flex items-center justify-between">
              <div>
                <p
                  className={`text-2xl font-bold ${theme === 'dark' ? 'text-amber-300' : 'text-amber-600'}`}
                >
                  {stats?.processing || 0}
                </p>
                <p
                  className={`text-xs font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}
                >
                  {t('tickets.status.processing')}
                </p>
              </div>
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-amber-500/20">
                <i
                  className={`fas fa-spinner ${theme === 'dark' ? 'text-amber-300' : 'text-amber-600'}`}
                ></i>
              </div>
            </div>
            <div className="absolute bottom-0 left-0 h-1 w-full bg-amber-500/40"></div>
          </div>

          {/* Resolved Stats */}
          <div className="relative overflow-hidden rounded-lg p-4 transition-all duration-300 hover:translate-y-[-2px] bg-indigo-500/10 hover:bg-indigo-500/15">
            <div className="flex items-center justify-between">
              <div>
                <p
                  className={`text-2xl font-bold ${theme === 'dark' ? 'text-indigo-300' : 'text-indigo-700'}`}
                >
                  {stats?.resolved || 0}
                </p>
                <p
                  className={`text-xs font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}
                >
                  {t('tickets.status.resolved')}
                </p>
              </div>
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-indigo-500/20">
                <i
                  className={`fas fa-check ${theme === 'dark' ? 'text-indigo-300' : 'text-indigo-600'}`}
                ></i>
              </div>
            </div>
            <div className="absolute bottom-0 left-0 h-1 w-full bg-indigo-500/40"></div>
          </div>

          {/* Closed Stats */}
          <div className="relative overflow-hidden rounded-lg p-4 transition-all duration-300 hover:translate-y-[-2px] bg-rose-500/10 hover:bg-rose-500/15">
            <div className="flex items-center justify-between">
              <div>
                <p
                  className={`text-2xl font-bold ${theme === 'dark' ? 'text-rose-300' : 'text-rose-700'}`}
                >
                  {stats?.closed || 0}
                </p>
                <p
                  className={`text-xs font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}
                >
                  {t('tickets.status.closed')}
                </p>
              </div>
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-rose-500/20">
                <i
                  className={`fas fa-archive ${theme === 'dark' ? 'text-rose-300' : 'text-rose-600'}`}
                ></i>
              </div>
            </div>
            <div className="absolute bottom-0 left-0 h-1 w-full bg-rose-500/40"></div>
          </div>
        </div>

        {/* Filters Row */}
        <div
          className={`flex flex-col space-y-4 transition-all duration-300 ${showFilters ? 'opacity-100' : 'opacity-0 h-0'}`}
          style={{
            transition: 'opacity 0.3s ease, height 0.3s ease',
            display: showFilters ? 'block' : 'none'
          }}
        >
          {/* First Row: Status, Sort, Order */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex-shrink-0">
              <select
                value={selectedStatus}
                onChange={(e) => handleStatusChange(e.target.value)}
                className={`block w-full pl-3 pr-10 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md transition-colors ${theme === 'dark'
                    ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600'
                    : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50'
                  }`}
              >
                <option value="" className={theme === 'dark' ? 'bg-gray-700' : 'bg-white'}>
                  {t('pages.tickets.allStatuses')}
                </option>
                {TICKET_STATUSES.map((status) => (
                  <option
                    key={status.value}
                    value={status.value}
                    className={theme === 'dark' ? 'bg-gray-700' : 'bg-white'}
                  >
                    {status.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex-shrink-0">
              <select
                value={sortBy}
                onChange={(e) => handleSortChange(e.target.value)}
                className={`block w-full pl-3 pr-10 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md transition-colors ${theme === 'dark'
                    ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600'
                    : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50'
                  }`}
              >
                <option value="createdAt" className={theme === 'dark' ? 'bg-gray-700' : 'bg-white'}>
                  {t('tickets.createdAt')}
                </option>
                <option value="updatedAt" className={theme === 'dark' ? 'bg-gray-700' : 'bg-white'}>
                  {t('tickets.updatedAt')}
                </option>
                <option value="priority" className={theme === 'dark' ? 'bg-gray-700' : 'bg-white'}>
                  {t('tickets.priority')}
                </option>
                <option value="status" className={theme === 'dark' ? 'bg-gray-700' : 'bg-white'}>
                  {t('tickets.columns.status')}
                </option>
              </select>
            </div>

            <button
              onClick={() => handleSortOrderChange(sortOrder === 'asc' ? 'desc' : 'asc')}
              className={`inline-flex items-center px-3 py-2 border text-sm leading-4 font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors ${theme === 'dark'
                  ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
            >
              {sortOrder === 'asc' ? '↑' : '↓'}
            </button>

            {/* View Toggle - Moved to the right */}
            <div className="flex-grow"></div>
            <div className="flex items-center">
              <span
                className={`mr-2 text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}
              >
                {t('pages.tickets.view')}:
              </span>
              <div
                className={`flex items-center rounded-lg ${theme === 'dark' ? 'bg-gray-700/70' : 'bg-gray-100'}`}
              >
                <button
                  onClick={() => toggleViewMode('table')}
                  className={`flex items-center justify-center px-3 py-2 rounded-md transition-all duration-200 ${viewMode === 'table'
                      ? `${theme === 'dark' ? 'bg-blue-500 text-white' : 'bg-blue-600 text-white'} shadow-sm`
                      : `${theme === 'dark' ? 'text-gray-300 hover:bg-gray-600/50' : 'text-gray-600 hover:bg-gray-200'}`
                    }`}
                  title={t('pages.tickets.tableView')}
                >
                  <i className="fas fa-list mr-1"></i>
                  <span className="text-xs font-medium">{t('pages.tickets.tableView')}</span>
                </button>
                <button
                  onClick={() => toggleViewMode('list')}
                  className={`flex items-center justify-center px-3 py-2 rounded-md transition-all duration-200 ${viewMode === 'list'
                      ? `${theme === 'dark' ? 'bg-blue-500 text-white' : 'bg-blue-600 text-white'} shadow-sm`
                      : `${theme === 'dark' ? 'text-gray-300 hover:bg-gray-600/50' : 'text-gray-600 hover:bg-gray-200'}`
                    }`}
                  title={t('pages.tickets.listView')}
                >
                  <i className="fas fa-table mr-1"></i>
                  <span className="text-xs font-medium">{t('pages.tickets.listView')}</span>
                </button>
                <button
                  onClick={() => toggleViewMode('kanban')}
                  className={`flex items-center justify-center px-3 py-2 rounded-md transition-all duration-200 ${viewMode === 'kanban'
                      ? `${theme === 'dark' ? 'bg-blue-500 text-white' : 'bg-blue-600 text-white'} shadow-sm`
                      : `${theme === 'dark' ? 'text-gray-300 hover:bg-gray-600/50' : 'text-gray-600 hover:bg-gray-200'}`
                    }`}
                  title={t('pages.tickets.kanbanView')}
                >
                  <i className="fas fa-columns mr-1"></i>
                  <span className="text-xs font-medium">{t('pages.tickets.kanbanView')}</span>
                </button>
              </div>
            </div>
          </div>

          {/* Second Row: User and Tags Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label
                className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}
              >
                {t('pages.tickets.requestedBy')}
              </label>
              <div className="w-full">
                <ThemedAsyncSelect
                  isMulti
                  cacheOptions={false}
                  defaultOptions={true}
                  loadOptions={loadRequestedByOptions}
                  value={requestedBy}
                  onChange={(newValue: any) => handleRequestedByChange(newValue)}
                  placeholder={t('pages.tickets.filterByRequesters')}
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              </div>
            </div>

            <div>
              <label
                className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}
              >
                {t('pages.tickets.assignedTo')}
              </label>
              <div className="w-full">
                <ThemedAsyncSelect
                  isMulti
                  cacheOptions={false}
                  defaultOptions={true}
                  loadOptions={loadAssignedToOptions}
                  value={assignedTo}
                  onChange={(newValue: any) => handleAssignedToChange(newValue)}
                  placeholder={t('pages.tickets.filterByAssignees')}
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              </div>
            </div>

            <div>
              <label
                className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}
              >
                {t('pages.tickets.tags')} ({allTags.length} available)
              </label>
              <div className="w-full">
                <ThemedAsyncSelect
                  isMulti
                  cacheOptions={false}
                  defaultOptions={allTags.map((tag: Tag) => ({ label: tag.name, value: tag.name }))}
                  loadOptions={loadTagOptions}
                  value={selectedTags.map((tagName) => ({ label: tagName, value: tagName }))}
                  onChange={(newValue: any) => handleTagsChange(newValue)}
                  placeholder={t('pages.tickets.filterByTags')}
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            itemsPerPage={itemsPerPage}
            itemsPerPageOptions={[12, 24, 50, 100]}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        )}

        {/* Main Content */}
        {isLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : tickets.length > 0 ? (
          <div className="mt-6">
            {viewMode === 'list' && (
              <TicketList
                tickets={tickets}
                currentPage={currentPage}
                onTicketClick={handleTicketClick}
              />
            )}
            {viewMode === 'table' && (
              <TicketTable
                tickets={tickets}
                currentPage={currentPage}
                onTicketClick={handleTicketClick}
              />
            )}
            {viewMode === 'kanban' && (
              <TicketKanbanBoard
                tickets={tickets}
                currentPage={currentPage}
                onUpdateTicketStatus={handleUpdateTicketStatus}
                onTicketClick={handleTicketClick}
              />
            )}
          </div>
        ) : (
          <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
            {t('pages.tickets.noTicketsFound')}
          </p>
        )}
      </Container>
    </DashboardLayout>
  );
}
